"use client";
import React, { useEffect, useState } from "react";
import Script from "next/script";
import CustomLiveagentButton from "@/components/ui/custom-liveagent-button";
import { sendGTMEvent } from "@next/third-parties/google";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";

declare global {
  interface WindowEventMap {
    chatbotState: CustomEvent;
    chatbotNavigate: CustomEvent;
  }
}

const getURLFromString = (stringWithURL: string) =>
  stringWithURL
    .replaceAll("\n", " ")
    .split(" ")
    .find((substringWithURL) => {
      try {
        return new URL(substringWithURL);
      } catch (_) {
        return false;
      }
    });

export default function Einsteinbot() {
  const [chatbotState, setChatbotState] = useState("initial");
  const [chatbotNavigateURL, setChatbotNavigateURL] = useState("");
  const pathname = usePathname();

  const setStateFromListener = (e: CustomEvent) => {
    setChatbotState(e.detail.type);
    sendGTMEvent({ event: "liveagent_listener", type: e.detail.type });
  };

  const setNavigateStateFromListener = (e: CustomEvent) => {
    setChatbotNavigateURL(e.detail.url);
  };

  // For debugging chatbot state
  // useEffect(() => console.log(chatbotState), [chatbotState]);

  // Handle automatic redirects
  useEffect(() => {
    if (!window) {
      return;
    }

    try {
      const validURL = new URL(chatbotNavigateURL);
      const agentMessageAfterLastUserMessage = document.querySelector(
        ".chatMessage.chasitor:nth-last-child(1 of .chasitor) + li .chatContent"
      );
      const lastURLSent = getURLFromString(
        (agentMessageAfterLastUserMessage as HTMLElement)?.innerText ?? ""
      );

      // If it's a new message, URL is a valid URL, URL to go to is not the
      // same as the current, and it's the last URL sent, redirect to URL.
      if (
        chatbotState === "chatbotUnreadMessage" &&
        validURL &&
        chatbotNavigateURL !== window.location.href &&
        chatbotNavigateURL === lastURLSent
      ) {
        // Cannot use router here because the third-party script gets
        // an error when there's a new message while loading the next page.
        setTimeout(() => {
          window.open(chatbotNavigateURL, "_self");
          // window.open(validURL.pathname, "_self");
        }, 1000);
      }
    } catch (_) {}
  }, [chatbotNavigateURL, chatbotState, pathname]);

  useEffect(() => {
    if (!window) {
      return;
    }

    window.addEventListener("chatbotState", setStateFromListener);
    window.addEventListener("chatbotNavigate", setNavigateStateFromListener);

    return () => {
      window.removeEventListener("chatbotState", setStateFromListener);
      window.removeEventListener(
        "chatbotNavigate",
        setNavigateStateFromListener
      );
    };
  });

  return (
    <>
      <Script
        type="text/javascript"
        id="einstein-bot-script"
        strategy="afterInteractive"
      >
        {`
          const initESW = function (gslbBaseURL) {
            embedded_svc.settings.displayHelpButton = false; //Or false
            embedded_svc.settings.language = ""; //For example, enter 'en' or 'en-US'

            //embedded_svc.settings.defaultMinimizedText = '...'; //(Defaults to Chat with an Expert)
            //embedded_svc.settings.disabledMinimizedText = '...'; //(Defaults to Agent Offline)

            //embedded_svc.settings.loadingText = ''; //(Defaults to Loading)
            //embedded_svc.settings.storageDomain = 'yourdomain.com'; //(Sets the domain for your deployment so that visitors can navigate subdomains during a chat session)

            // Settings for Chat
            //embedded_svc.settings.directToButtonRouting = function(prechatFormData) {
            // Dynamically changes the button ID based on what the visitor enters in the pre-chat form.
            // Returns a valid button ID.
            //};
            //embedded_svc.settings.prepopulatedPrechatFields = {}; //Sets the auto-population of pre-chat form fields
            //embedded_svc.settings.fallbackRouting = []; //An array of button IDs, user IDs, or userId_buttonId
            //embedded_svc.settings.offlineSupportMinimizedText = '...'; //(Defaults to Contact Us)

            //Chatbot Avatar
            embedded_svc.settings.chatbotAvatarImgURL =
              "https://careindeed.my.site.com/library/resource/1723042122000/CILiveAgentAvatar?";

            //Agent Avatar
            embedded_svc.settings.avatarImgURL =
              "https://careindeed.my.site.com/library/resource/1723042122000/CILiveAgentAvatar?";

            //Chat Header
            embedded_svc.settings.smallCompanyLogoImgURL =
              "https://careindeed.my.site.com/library/resource/1723072103000/CILiveAgentHeader?";

            embedded_svc.settings.enabledFeatures = ["LiveAgent"];
            embedded_svc.settings.entryFeature = "LiveAgent";

            // Event called on chat loaded or if there are no agents
            embedded_svc.addEventHandler(
              "onSettingsCallCompleted",
              // Can get "isAgentAvailable" for function https://developer.salesforce.com/docs/atlas.en-us.232.0.snapins_web_dev.meta/snapins_web_dev/snapins_web_chat_events.htm
              function (a) {
                const chatbotLoaded = new CustomEvent("chatbotState", {
                  detail: {
                    type: a.isAgentAvailable ? "chatbotLoaded" : "chatbotLoadedNoAgents",
                    // type: "chatbotLoaded",
                  },
                });
                window.dispatchEvent(chatbotLoaded);
                // console.log("onSettingsCallCompleted", a);
              }
            );

            // Event called on loading
            embedded_svc.addEventHandler("beforeCreate", function (a) {
              const chatbotWindowLoading = new CustomEvent("chatbotState", {
                detail: {
                  type: "chatbotWindowLoading",
                },
              });
              window.dispatchEvent(chatbotWindowLoading);
              // console.log("beforeCreate", a);
            });

            // Event called when chat is maximized
            embedded_svc.addEventHandler("afterMaximize", function (a) {
              const chatbotWindowLoaded = new CustomEvent("chatbotState", {
                detail: {
                  type: "chatbotWindowLoaded",
                },
              });
              window.dispatchEvent(chatbotWindowLoaded);
              // console.log("afterMaximize", a);
            });

            // Event called when chat is minimized
            embedded_svc.addEventHandler("afterMinimize", function (a) {
              const chatbotWindowLoaded = new CustomEvent("chatbotState", {
                detail: {
                  type: "chatbotWindowLoaded",
                },
              });
              window.dispatchEvent(chatbotWindowLoaded);
              // console.log("afterMinimize", a);
            });

            // Event called when chat is closed
            embedded_svc.addEventHandler("afterDestroy", function (a) {
              const chatbotLoaded = new CustomEvent("chatbotState", {
                detail: {
                  type: "chatbotLoaded",
                },
              });
              window.dispatchEvent(chatbotLoaded);
              // console.log("afterDestroy", a);
            });

            // Event called when chat has a message
            embedded_svc.addEventHandler("onAgentMessage", function (a) {
              const chatbotUnreadMessage = new CustomEvent("chatbotState", {
                detail: {
                  type: "chatbotUnreadMessage",
                },
              });
              window.dispatchEvent(chatbotUnreadMessage);
              // console.log("onAgentMessage", a);
            });

            // Event called when chat has a message
            embedded_svc.addEventHandler("onAgentRichMessage", function (a) {
              const chatbotUnreadMessage = new CustomEvent("chatbotState", {
                detail: {
                  type: "chatbotUnreadMessage",
                },
              });
              window.dispatchEvent(chatbotUnreadMessage);
              // console.log("onAgentRichMessage", a);
            });

            // Continuously check for agent status (60 seconds)
            // Buggy: Will make icon reappear even when minimized
            // embedded_svc.addEventHandler("onAvailability", function (a) {
            //   const chatbotLoaded = new CustomEvent("chatbotState", {
            //     detail: {
            //       type: a.isAgentAvailable ? "chatbotLoaded" : "chatbotLoadedNoAgents",
            //     },
            //   });
            //   window.dispatchEvent(chatbotLoaded);
            // });


            embedded_svc.init(
              "https://careindeed.my.salesforce.com",
              "https://careindeed.my.site.com/library",
              gslbBaseURL,
              "00D1I000001V6BJ",
              "Care_Indeed_Live_Agent_Demo",
              {
                baseLiveAgentContentURL:
                  "https://c.la1-core1.sfdc-lywfpd.salesforceliveagent.com/content",
                deploymentId: "5721I0000005Vhr",
                buttonId: "573Rk0000003Rzl",
                baseLiveAgentURL:
                  "https://d.la1-core1.sfdc-lywfpd.salesforceliveagent.com/chat",
                eswLiveAgentDevName:
                  "EmbeddedServiceLiveAgent_Parent04IRk0000008AWrMAM_19129a23d13",
                isOfflineSupportEnabled: false,
              }
            );


          };

          if (!window.embedded_svc) {
            const s = document.createElement("script");
            s.setAttribute(
              "src",
              "https://careindeed.my.salesforce.com/embeddedservice/5.0/esw.min.js"
            );
            s.onload = function () {
              initESW(null);
            };
            document.body.appendChild(s);
          } else {
            initESW("https://service.force.com");
          }
        `}
      </Script>

      <CustomLiveagentButtonLazy
        variant={chatbotState}
        floating
      />
    </>
  );
}

const CustomLiveagentButtonLazy = dynamic(
  () => import("../ui/custom-liveagent-button"),
  {
    ssr: false,
    loading: () => (
      <CustomLiveagentButton
        variant="initialNoClose"
        floating
      />
    ),
  }
);
